import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Calendar, Ticket, Users, Hourglass, Gift, Megaphone, Sparkles, Settings, LayoutDashboard, Bell, BarChart3, Users2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import EventsList from "@/components/admin/EventsList";
import CapacityAlertsBanner from "@/components/admin/CapacityAlertsBanner";
import CreateEventForm from "@/components/admin/CreateEventForm";
import TicketsList from "@/components/admin/TicketsList";
import UsersList from "@/components/admin/UsersList";
import BingoResponseList from "@/components/admin/BingoResponseList";
import SalonWaitlist from "@/components/admin/SalonWaitlist";
import AdventPassList from "@/components/admin/AdventPassList";
import PartnerCodesList from "@/components/admin/PartnerCodesList";
import CampaignsList from "@/components/admin/CampaignsList";
import WrappedAdmin from "@/components/admin/WrappedAdmin";
import { AdminSettings } from "@/components/admin/AdminSettings";
import HomepageBannersList from "@/components/admin/HomepageBannersList";
import { NotificationsTab } from "@/components/admin/notifications/NotificationsTab";
import { AnalyticsTab } from "@/components/admin/AnalyticsTab";
import { CircleTab } from "@/components/admin/circle/CircleTab";
import { useWrappedEnabled } from "@/hooks/useWrappedEnabled";

const VALID_TABS = ["events", "tickets", "users", "partner-codes", "salon-waitlist", "advent-pass", "campaigns", "bingo-responses", "wrapped", "homepage-banners", "notifications", "analytics", "circle", "settings"];

const Admin = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const currentTab = tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : "events";
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const { isWrappedEnabled, isLoading: isWrappedLoading } = useWrappedEnabled();

  // Only show wrapped tab when loaded and enabled
  const showWrappedTab = !isWrappedLoading && isWrappedEnabled;

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  return (
    <div className="p-4 w-full max-w-full ph-no-capture">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-foreground">Admin Console</h1>
        <Button 
          onClick={() => setShowCreateEvent(true)} 
          className="gap-2"
          variant="secondary"
        >
          <Plus className="w-4 h-4" />
          Create Event
        </Button>
      </div>

      {showCreateEvent ? (
        <CreateEventForm onClose={() => setShowCreateEvent(false)} />
      ) : (
        <Tabs value={currentTab} onValueChange={handleTabChange} className="w-full">
          <ScrollArea className="w-full mb-8">
            <TabsList className="inline-flex w-auto min-w-full">
              <TabsTrigger value="events" className="gap-2 whitespace-nowrap">
                <Calendar className="w-4 h-4" />
                Events
              </TabsTrigger>
              <TabsTrigger value="tickets" className="gap-2 whitespace-nowrap">
                <Ticket className="w-4 h-4" />
                Tickets
              </TabsTrigger>
              <TabsTrigger value="users" className="gap-2 whitespace-nowrap">
                <Users className="w-4 h-4" />
                Users
              </TabsTrigger>
              <TabsTrigger value="partner-codes" className="gap-2 whitespace-nowrap">
                <Ticket className="w-4 h-4" />
                Partner Codes
              </TabsTrigger>
              <TabsTrigger value="salon-waitlist" className="gap-2 whitespace-nowrap">
                <Hourglass className="w-4 h-4" />
                Salon Waitlist
              </TabsTrigger>
              {/* <TabsTrigger value="advent-pass" className="gap-2 whitespace-nowrap">
                <Gift className="w-4 h-4" />
                Advent Pass
              </TabsTrigger> */}
              <TabsTrigger value="campaigns" className="gap-2 whitespace-nowrap">
                <Megaphone className="w-4 h-4" />
                Campaigns
              </TabsTrigger>
              {/* <TabsTrigger value="bingo-responses" className="gap-2 whitespace-nowrap">
                <Users className="w-4 h-4" />
                Bingo Responses
              </TabsTrigger> */}
              {showWrappedTab && (
                <TabsTrigger value="wrapped" className="gap-2 whitespace-nowrap">
                  <Sparkles className="w-4 h-4" />
                  Wrapped
                </TabsTrigger>
              )}
              <TabsTrigger value="homepage-banners" className="gap-2 whitespace-nowrap">
                <LayoutDashboard className="w-4 h-4" />
                Banners
              </TabsTrigger>
              <TabsTrigger value="notifications" className="gap-2 whitespace-nowrap">
                <Bell className="w-4 h-4" />
                Notifications
              </TabsTrigger>
              <TabsTrigger value="analytics" className="gap-2 whitespace-nowrap">
                <BarChart3 className="w-4 h-4" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="circle" className="gap-2 whitespace-nowrap">
                <Users2 className="w-4 h-4" />
                Circle
              </TabsTrigger>
              <TabsTrigger value="settings" className="gap-2 whitespace-nowrap">
                <Settings className="w-4 h-4" />
                Settings
              </TabsTrigger>
            </TabsList>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
          
          <TabsContent value="events">
            <CapacityAlertsBanner />
            <EventsList />
          </TabsContent>
          
          <TabsContent value="tickets">
            <TicketsList />
          </TabsContent>
          
          <TabsContent value="users">
            <UsersList />
          </TabsContent>
          
          <TabsContent value="partner-codes">
            <PartnerCodesList />
          </TabsContent>
          
          <TabsContent value="salon-waitlist">
            <SalonWaitlist />
          </TabsContent>
          
          <TabsContent value="advent-pass">
            <AdventPassList />
          </TabsContent>
          
          <TabsContent value="campaigns">
            <CampaignsList />
          </TabsContent>
          
          <TabsContent value="bingo-responses">
            <BingoResponseList />
          </TabsContent>
          
          <TabsContent value="wrapped">
            <WrappedAdmin />
          </TabsContent>
          
          <TabsContent value="homepage-banners">
            <HomepageBannersList />
          </TabsContent>
          
          <TabsContent value="notifications">
            <NotificationsTab />
          </TabsContent>
          
          <TabsContent value="analytics">
            <AnalyticsTab />
          </TabsContent>
          
          <TabsContent value="circle">
            <CircleTab />
          </TabsContent>
          
          <TabsContent value="settings">
            <AdminSettings />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default Admin;
